import type { AccountDetails } from "@/apis/gql/types";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { NewAccountForm } from "@/components/shared/form/new-account";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { AutocompleteInput, type Option } from "./autocomplete-input";

type Props = {
  name: string;
  placeholder: string;
  label?: string;
  options: AccountDetails[];
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  autoFocus?: boolean;
  showEmptyLabel?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (value: Option["value"]) => void;
  nullable?: boolean;
};

export const AccountAutocompleteInput = ({
  name,
  placeholder,
  label,
  options,
  onKeyDown,
  side,
  sideOffset,
  showEmptyLabel,
  autoFocus,
  onFocus,
  onBlur,
  nullable,
}: Props) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accountOptions = useMemo(() => {
    return (
      options?.map((account) => ({
        value: account.accountId,
        label: account.accountName,
        subLabel: account.accountNo,
      })) ?? []
    );
  }, [options]);

  const filterByAccount = (
    inputValue: string,
    value: Option["value"],
    label: Option["label"],
    subLabel: Option["subLabel"],
  ) => {
    const _search = inputValue.replace(/ /g, "");
    const _label = label?.replace(/ /g, "") ?? "";
    const _subLabel = subLabel?.replace(/ /g, "") ?? "";

    return (
      _label.toLowerCase().includes(_search.toLowerCase()) ||
      _subLabel.toLowerCase().includes(_search.toLowerCase())
    );
  };

  const handleDialogOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const form = useFormContext();

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <AutocompleteInput
        ref={inputRef}
        name={name}
        label={label}
        placeholder={placeholder}
        options={accountOptions}
        onKeyDown={onKeyDown}
        filterBy={filterByAccount}
        EmptyListComponent={(props) => (
          <EmptyMessage {...props} onClick={(val) => setOpen(val)} />
        )}
        side={side}
        sideOffset={sideOffset}
        autoFocus={autoFocus}
        onBlur={onBlur}
        onFocus={onFocus}
        showEmptyLabel={showEmptyLabel}
        nullable={nullable}
      />
      <DialogContent className="sm:max-w-[425px] z-index-[100]">
        <NewAccountForm
          initialValues={{
            accountNo: /^-?\d+$/.test(inputRef.current?.value ?? "")
              ? inputRef.current?.value ?? ""
              : "",
            accountName:
              /^-?\d+$/.test(inputRef.current?.value ?? "") === false
                ? inputRef.current?.value ?? ""
                : "",
            accountDescription: "",
          }}
          onSuccess={(accountId) => {
            form.setValue(name, accountId);
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

type EmptyMessageProps = {
  inputValue: string;
  onClick: (value: boolean) => void;
};

const EmptyMessage = ({ onClick }: EmptyMessageProps) => {
  const { t } = useTranslation();

  return (
    <>
      <DialogTrigger
        asChild
        className="flex flex-row gap-1 items-center cursor-pointer px-2 w-full"
      >
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick(true);
          }}
          className="gap-2"
        >
          <Plus size={16} />
          {`${t("accounts.new")}...`}
        </Button>
      </DialogTrigger>
    </>
  );
};
